import { DropDown, requiredRule, ValidatedInput } from '@finbeat/react-toolkit';
import { ServiceDiscountTable } from 'models/api/serviceDiscount';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, ModalBody, Row } from 'reactstrap';

type ServiceDiscountChangeModalBodyProps = {
    disable: boolean;
    intervalError: boolean;
    onChangeIntervalError: (value: boolean) => void;
    tariffPlans: DropDown[];
};

const ServiceDiscountChangeModalBody = ({
    disable,
    intervalError,
    onChangeIntervalError,
    tariffPlans,
}: ServiceDiscountChangeModalBodyProps) => {
    const { t } = useTranslation();
    const { watch, setError, clearErrors, setFocus } = useFormContext<ServiceDiscountTable>();
    const [previousStartAmount, setPreviousStartAmount] = useState(0);
    const [previousEndAmount, setPreviousEndAmount] = useState(0);
    const intervalErrorMessage = t(
        'BackOfficeSpecifiedIntervalOfAmountsOverlapsWithExistingOneMsg'
    );

    useEffect(() => {
        intervalValidator(intervalError);
    }, [intervalError]);

    const startAmountValidator = () => {
        const startAmount = watch('startAmount');
        const endAmount = watch('endAmount');

        if (previousStartAmount !== startAmount) onChangeIntervalError(false);

        intervalValidator(intervalError);

        if (startAmount > endAmount) return t('ServiceDiscountStartAmountErrorSubsequence');
    };

    const endAmountValidator = () => {
        const startAmount = watch('startAmount');
        const endAmount = watch('endAmount');

        if (previousEndAmount !== endAmount) onChangeIntervalError(false);

        intervalValidator(intervalError);

        if (startAmount > endAmount) return t('ServiceDiscountEndAmountErrorSubsequence');
    };

    const intervalValidator = (isError: boolean) => {
        setPreviousStartAmount(watch('startAmount'));
        setPreviousEndAmount(watch('endAmount'));

        if (isError) {
            setError('startAmount', {
                message: intervalErrorMessage,
            });

            setError('endAmount', {
                message: intervalErrorMessage,
            });

            setTimeout(() => setFocus('startAmount'), 1);
        } else {
            clearErrors(['startAmount', 'endAmount']);
        }
    };

    return (
        <ModalBody className="custom-scrollbar">
            <Row>
                <Col>
                    <ValidatedInput
                        type="select"
                        id="ds-tariff-plan-id-id"
                        name="tariffPlanId"
                        label={t('BackOfficeTariffPlan')}
                        options={[<option value="" key="-1"></option>].concat(
                            tariffPlans.map((i) => (
                                <option value={i.id ?? undefined} key={i.id}>
                                    {i.name}
                                </option>
                            ))
                        )}
                        rules={{
                            required: requiredRule,
                            validate: startAmountValidator,
                        }}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <ValidatedInput
                        label={t('BackOfficeStartAmount')}
                        type="number"
                        name="startAmount"
                        rules={{
                            required: requiredRule,
                            validate: startAmountValidator,
                        }}
                        disabled={disable}
                    />
                </Col>
                <Col>
                    <ValidatedInput
                        label={t('BackOfficeEndAmount')}
                        type="number"
                        name="endAmount"
                        rules={{
                            required: requiredRule,
                            validate: endAmountValidator,
                        }}
                        disabled={disable}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <ValidatedInput
                        label={t('BackOfficeApprovalFeeDiscount')}
                        name="approvalFeeDiscount"
                        type="number"
                        disabled={disable}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <ValidatedInput
                        label={t('BackOfficeLoanServiceFeeDiscount')}
                        name="loanServiceFeeDiscount"
                        type="number"
                        disabled={disable}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <ValidatedInput
                        label={t('BackOfficeGuaranteeFeePercent')}
                        name="guaranteeFeePercent"
                        type="number"
                        disabled={disable}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <ValidatedInput
                        label={t('BackOfficeExtensionApprovalFeeDiscount')}
                        name="extensionApprovalFeeDiscount"
                        type="number"
                        disabled={disable}
                    />
                </Col>
            </Row>
        </ModalBody>
    );
};

export default ServiceDiscountChangeModalBody;
